/**
 * Библиотека текстур отделки — обои, плитка, панели, штукатурка, дерево, паркет, линолеум —
 * и обивки мебели: ткани и кожа.
 *
 * Текстур тысячи, и их список весит сотни килобайт, поэтому в стартовый код он не входит:
 * это отдельный кусок (`textureLibrary.data.ts`, генерирует `npm run textures`), который
 * 3D-вид догружает при открытии (`loadTextureLibrary`), пока виден лоадер. До загрузки
 * списки пустые: id текстуры в проекте не находится, и стена или пол рисуются без неё —
 * запись в проекте при этом не теряется.
 *
 * В куске данные сжаты в строки-кортежи; здесь они разворачиваются в `FinishTexture`.
 * Пути к картинкам выводятся из вида и id: `/textures/<вид>/<id>.webp`, миниатюра — `<id>-s.webp`.
 */
import type { FabricTextureKind, FinishTexture, FloorTextureKind, WallTextureKind } from "./types";

type TextureKind = WallTextureKind | FloorTextureKind | FabricTextureKind;

/** Строка куска: id, вид (номер в `kinds`), коллекция (номер), имя, ширина и высота в мм, цвет. */
export type TextureRow = [string, number, number, string, number, number, string];

export interface TextureLibraryData {
	kinds: readonly TextureKind[];
	collections: readonly string[];
	rows: readonly TextureRow[];
}

const FLOOR_KINDS: ReadonlySet<TextureKind> = new Set<FloorTextureKind>([
	"wood",
	"parquet",
	"linoleum",
]);

const FABRIC_KINDS: ReadonlySet<TextureKind> = new Set<FabricTextureKind>(["fabric", "leather"]);

let wall: readonly FinishTexture[] = [];
let floor: readonly FinishTexture<FloorTextureKind>[] = [];
let fabric: readonly FinishTexture<FabricTextureKind>[] = [];
let fabricById: ReadonlyMap<string, FinishTexture<FabricTextureKind>> = new Map();
let loaded = false;
let loading: Promise<void> | null = null;

const expand = ({ kinds, collections, rows }: TextureLibraryData) => {
	const nextWall: FinishTexture[] = [];
	const nextFloor: FinishTexture<FloorTextureKind>[] = [];
	const nextFabric: FinishTexture<FabricTextureKind>[] = [];
	for (const [id, kindIndex, collectionIndex, name, width, height, color] of rows) {
		const kind = kinds[kindIndex];
		const texture = {
			id,
			kind,
			collection: collections[collectionIndex],
			name,
			src: `/textures/${kind}/${id}.webp`,
			thumb: `/textures/${kind}/${id}-s.webp`,
			width,
			height,
			color,
		};
		if (FLOOR_KINDS.has(kind)) nextFloor.push(texture as FinishTexture<FloorTextureKind>);
		else if (FABRIC_KINDS.has(kind)) nextFabric.push(texture as FinishTexture<FabricTextureKind>);
		else nextWall.push(texture as FinishTexture);
	}
	wall = nextWall;
	floor = nextFloor;
	fabric = nextFabric;
	fabricById = new Map(nextFabric.map((texture) => [texture.id, texture]));
	loaded = true;
};

/** Текстуры стен: обои, плитка, панели, штукатурка. Пока библиотека не загружена — пусто. */
export const wallTextures = (): readonly FinishTexture[] => wall;

/** Текстуры пола: дерево, паркет, линолеум. Плитку пол берёт из текстур стен. */
export const floorTextures = (): readonly FinishTexture<FloorTextureKind>[] => floor;

/** Текстуры обивки мебели: ткани и кожа. Пока библиотека не загружена — пусто. */
export const fabricTextures = (): readonly FinishTexture<FabricTextureKind>[] => fabric;

/** Ткань или кожа по id; null — нет такой или библиотека ещё не загружена. */
export const fabricTextureById = (id: string): FinishTexture<FabricTextureKind> | null =>
	fabricById.get(id) ?? null;

export const isTextureLibraryLoaded = (): boolean => loaded;

/** Догружает библиотеку один раз; повторный вызов отдаёт ту же загрузку. Сбой — можно повторить. */
export const loadTextureLibrary = (): Promise<void> => {
	loading ??= import("./textureLibrary.data").then(
		(module) => expand(module.TEXTURE_LIBRARY),
		(error: unknown) => {
			loading = null;
			throw error;
		},
	);
	return loading;
};
